import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { generateMessage, generateFollowUp } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { usePayment } from "../contexts/PaymentContext";
import HistorySidebar from "./HistorySidebar";
import RemindMeButton from "./RemindMeButton";
import RemindersPanel from "./RemindersPanel";
import { useReminders } from "../hooks/useReminders";
import styles from "./ReferralForm.module.css";

const LAST_RECORD_KEY = "rc_lastRecord";

const ReferralForm = () => {
  const { currentUser } = useAuth();
  const { status, fetchStatus, triggerUpgrade } = usePayment();
  const {
    reminders,
    isLoading: remindersLoading,
    isCancelling,
    createReminder,
    cancelReminder,
  } = useReminders();

  const [remindersSidebarOpen, setRemindersSidebarOpen] = useState(false);

  // After a reminder is created, briefly open the sidebar to teach the user
  const handleReminderCreated = useCallback(() => {
    setRemindersSidebarOpen(true);
    setTimeout(() => setRemindersSidebarOpen(false), 2500);
  }, []);

  // ── Load last record from localStorage on mount ──
  const savedRecord = (() => {
    try {
      const raw = localStorage.getItem(LAST_RECORD_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const [formData, setFormData] = useState(
    savedRecord?.formData || {
      jobLink: "",
      company: "",
      role: "",
      experience: "",
      skills: "",
      college: "",
      targetPerson: "",
    }
  );

  const [message, setMessage] = useState(savedRecord?.message || "");
  const [loading, setLoading] = useState(false);
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [editedMessage, setEditedMessage] = useState(savedRecord?.message || "");
  const [copied, setCopied] = useState(false);
  const [isTemplate, setIsTemplate] = useState(false);
  const [promo, setPromo] = useState(null);      // { hint, cta, action } from API
  const [promoDismissed, setPromoDismissed] = useState(false);
  const [responseScore, setResponseScore] = useState(savedRecord?.responseScore || null);
  const [scoreExpanded, setScoreExpanded] = useState(false);

  // Scroll indicator — shows a fade + chevron when more content is below
  const scrollRef = useRef(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // More than 12px remaining below the fold
    setHasMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 12);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();                                    // initial check
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ── LinkedIn auto-fill (extension only) ──
  const isExtension = process.env.REACT_APP_EXTENSION === "true";
  const [linkedinStatus, setLinkedinStatus] = useState(null); // null | "loading" | "success" | "not-linkedin" | "error"

  const applyLinkedInData = useCallback((d) => {
    setFormData((prev) => ({
      ...prev,
      ...(d.company ? { company: d.company } : {}),
      ...(d.role ? { role: d.role } : {}),
      ...(d.jobLink ? { jobLink: d.jobLink } : {}),
      ...(d.skills ? { skills: d.skills } : {}),
      ...(d.targetPerson ? { targetPerson: d.targetPerson } : {}),
    }));
    setLinkedinStatus("success");
    setTimeout(() => setLinkedinStatus(null), 3000);
  }, []);

  const importFromLinkedIn = useCallback(() => {
    if (!isExtension) return;
    /* global chrome */
    setLinkedinStatus("loading");

    chrome.tabs.query({ active: true, currentWindow: true }, (tabs) => {
      const tab = tabs?.[0];
      if (!tab?.url || !tab.url.includes("linkedin.com")) {
        setLinkedinStatus("not-linkedin");
        setTimeout(() => setLinkedinStatus(null), 3000);
        return;
      }

      // Try sending message to existing content script first.
      // If it fails (content script not injected yet), inject it programmatically.
      const handleResponse = (response) => {
        if (chrome.runtime.lastError || !response?.success) {
          // Content script might not be injected — try programmatic injection
          if (chrome.scripting?.executeScript) {
            chrome.scripting.executeScript(
              { target: { tabId: tab.id }, files: ["content-linkedin.js"] },
              () => {
                if (chrome.runtime.lastError) {
                  setLinkedinStatus("error");
                  setTimeout(() => setLinkedinStatus(null), 3000);
                  return;
                }
                // Retry after injection
                setTimeout(() => {
                  chrome.tabs.sendMessage(tab.id, { type: "RC_EXTRACT_LINKEDIN" }, (retryResponse) => {
                    if (chrome.runtime.lastError || !retryResponse?.success) {
                      setLinkedinStatus("error");
                      setTimeout(() => setLinkedinStatus(null), 3000);
                      return;
                    }
                    applyLinkedInData(retryResponse.data);
                  });
                }, 300);
              }
            );
          } else {
            // Fallback: scripting API not available — ask user to refresh
            setLinkedinStatus("error");
            setTimeout(() => setLinkedinStatus(null), 3000);
          }
          return;
        }
        applyLinkedInData(response.data);
      };

      chrome.tabs.sendMessage(tab.id, { type: "RC_EXTRACT_LINKEDIN" }, handleResponse);
    });
  }, [isExtension, applyLinkedInData]);

  // Auto-fill on popup open when on a LinkedIn page (extension only)
  const autoFillRan = useRef(false);
  useEffect(() => {
    if (!isExtension || autoFillRan.current) return;
    // Only auto-fill if the form is empty (no saved record)
    const isEmpty = !formData.company && !formData.role;
    if (isEmpty) {
      autoFillRan.current = true;
      importFromLinkedIn();
    }
  }, [isExtension, formData.company, formData.role, importFromLinkedIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company || !formData.role) {
      alert("Company and Role are required.");
      return;
    }

    // ── Always call the API — backend returns a template for guests,
    // an AI-generated message for authenticated users.
    try {
      setLoading(true);
      const res = await generateMessage(formData);
      setMessage(res.data.message);
      setEditedMessage(res.data.message);
      setResponseScore(res.data.responseScore || null);
      // Flag template responses so the upsell strip is shown
      setIsTemplate(!currentUser);
      // Capture promo data returned by the backend for guest users
      if (res.data.promo) {
        setPromo(res.data.promo);
        setPromoDismissed(false);
      }
      // Persist last record in localStorage for everyone
      try {
        localStorage.setItem(
          LAST_RECORD_KEY,
          JSON.stringify({ formData, message: res.data.message, responseScore: res.data.responseScore })
        );
      } catch { /* quota exceeded — ignore */ }
      // Refresh credit/plan status after each AI generation
      if (currentUser) fetchStatus();
    } catch (err) {
      // 429 is handled by the API interceptor → opens UpgradeModal
      if (err.response?.status !== 429) {
        alert("Error generating message. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(editedMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFollowUp = async () => {
    if (!message) return;
    try {
      setFollowUpLoading(true);
      const res = await generateFollowUp({
        ...formData,
        originalMessage: message,
      });
      setMessage(res.data.message);
      setEditedMessage(res.data.message);
      setResponseScore(res.data.responseScore || null);
      setIsTemplate(!currentUser);
      if (res.data.promo) {
        setPromo(res.data.promo);
        setPromoDismissed(false);
      }
      try {
        localStorage.setItem(
          LAST_RECORD_KEY,
          JSON.stringify({ formData, message: res.data.message, responseScore: res.data.responseScore })
        );
      } catch { /* quota exceeded */ }
      if (currentUser) fetchStatus();
    } catch (err) {
      if (err.response?.status !== 429) {
        alert("Error generating follow-up. Please try again.");
      }
    } finally {
      setFollowUpLoading(false);
    }
  };

  // ── History sidebar: load a past record into the form ──
  const handleHistorySelect = useCallback(({ formData: fd, message: msg }) => {
    setFormData({
      jobLink: fd.jobLink || "",
      company: fd.company || "",
      role: fd.role || "",
      experience: fd.experience || "",
      skills: fd.skills || "",
      college: fd.college || "",
      targetPerson: fd.targetPerson || "",
    });
    setMessage(msg);
    setEditedMessage(msg);
    setIsTemplate(false);
    setPromo(null);
    setPromoDismissed(false);
    setResponseScore(null);
  }, []);

  return (
    <>
      {/* History sidebar — logged-in users only */}
      {currentUser && <HistorySidebar onSelect={handleHistorySelect} />}

      {/* Reminders sidebar — logged-in users only */}
      {currentUser && (
        <RemindersPanel
          reminders={reminders}
          isLoading={remindersLoading}
          isCancelling={isCancelling}
          onCancelReminder={cancelReminder}
          open={remindersSidebarOpen}
          onOpenChange={setRemindersSidebarOpen}
        />
      )}

      <div className={styles.container}>

      {/* ── LEFT: INPUT FORM (60%) ── */}
      <div className={styles.leftPanel}>
        <div className={styles.formCard}>

          {/* Header — pinned top */}
          <div className={styles.formHeader}>
            <div className={styles.formHeaderRow}>
              <h2 className={styles.formTitle}>🚀 Referral Generator</h2>
              {isExtension && (
                <button
                  type="button"
                  className={styles.linkedinImportBtn}
                  onClick={importFromLinkedIn}
                  disabled={linkedinStatus === "loading"}
                >
                  {linkedinStatus === "loading" ? "⏳ Importing..." :
                   linkedinStatus === "success" ? "✅ Imported" :
                   linkedinStatus === "not-linkedin" ? "⚠️ Open LinkedIn" :
                   linkedinStatus === "error" ? "❌ Retry" :
                   "🔗 LinkedIn"}
                </button>
              )}
            </div>
            <p className={styles.formSubtitle}>
              Fill in your details to craft a professional referral message
            </p>
          </div>

          {/* Scrollable fields + fade indicator wrapper */}
          <form onSubmit={handleSubmit} style={{ display: "contents" }}>
            <div className={styles.scrollContainer}>
              <div
                ref={scrollRef}
                className={styles.formScrollable}
                onScroll={checkScroll}
              >
                {/* Company + Role */}
                <div className={styles.formGroupRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Company *</label>
                    <input
                      id="field-company"
                      name="company"
                      placeholder="e.g. Google"
                      className={styles.formInput}
                      value={formData.company}
                      onChange={handleChange}
                      required
                      autoComplete="organization"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Role *</label>
                    <input
                      id="field-role"
                      name="role"
                      placeholder="e.g. Software Engineer"
                      className={styles.formInput}
                      value={formData.role}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Job Link */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Job Link</label>
                  <input
                    id="field-jobLink"
                    name="jobLink"
                    type="url"
                    placeholder="https://linkedin.com/jobs/..."
                    className={styles.formInput}
                    value={formData.jobLink}
                    onChange={handleChange}
                    autoComplete="url"
                  />
                </div>

                {/* Experience + Skills */}
                <div className={styles.formGroupRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Experience</label>
                    <input
                      id="field-experience"
                      name="experience"
                      placeholder="e.g. 2 yrs in Backend"
                      className={styles.formInput}
                      value={formData.experience}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Skills</label>
                    <input
                      id="field-skills"
                      name="skills"
                      placeholder="e.g. React, Node.js"
                      className={styles.formInput}
                      value={formData.skills}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* College + Target Person */}
                <div className={styles.formGroupRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>College</label>
                    <input
                      id="field-college"
                      name="college"
                      placeholder="e.g. IIT Delhi"
                      className={styles.formInput}
                      value={formData.college}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Target Person</label>
                    <input
                      id="field-targetPerson"
                      name="targetPerson"
                      placeholder="Referrer name / title"
                      className={styles.formInput}
                      value={formData.targetPerson}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Scroll-fade indicator — visible only when content below */}
              <div
                className={`${styles.scrollFade} ${hasMoreBelow ? styles.scrollFadeVisible : ""}`}
                aria-hidden="true"
              >
                <span className={styles.scrollChevron}>⌄</span>
              </div>
            </div>

            {/* Submit — pinned bottom of card */}
            <div className={styles.submitFooter}>
              {/* AI spark banner — visible only for guests */}
              {!currentUser && (
                <Link to="/ai-features" className={styles.aiSparkBar}>
                  <span className={styles.aiSparkOrb} aria-hidden="true" />
                  <span className={styles.aiSparkIcon}>⚡</span>
                  <span className={styles.aiSparkText}>
                    <strong>AI-powered messages</strong> are 3× more effective
                  </span>
                  <span className={styles.aiSparkCta}>Unlock AI →</span>
                </Link>
              )}

              <button
                id="btn-generate"
                type="submit"
                className={`${styles.submitButton} ${!currentUser ? styles.submitButtonGuest : ""}`}
                disabled={loading}
                aria-busy={loading}
              >
                {loading
                  ? "⚙️ Generating…"
                  : currentUser
                  ? "✨ Generate with AI"
                  : "📄 Preview with Template"}
              </button>

              {/* Usage hint + urgency nudges for logged-in users */}
              {currentUser && status && (() => {
                const isUnlimited = status.unlimited?.active;
                const credits = status.credits ?? 0;
                const dailyUsed = status.freeUsage?.used ?? 0;
                const dailyLimit = status.freeUsage?.limit ?? 5;
                const dailyLeft = Math.max(0, dailyLimit - dailyUsed);
                const hasPaid = credits > 0;

                if (isUnlimited) {
                  return <p className={styles.dailyHint}>⚡ Unlimited AI access active</p>;
                }

                if (hasPaid && credits <= 5) {
                  return (
                    <p className={`${styles.dailyHint} ${styles.dailyHintUrgent}`}
                       onClick={() => triggerUpgrade({ message: `Only ${credits} credit${credits !== 1 ? "s" : ""} left!` })}>
                      🔥 Only {credits} credit{credits !== 1 ? "s" : ""} left — <span className={styles.dailyHintLink}>top up now</span>
                    </p>
                  );
                }

                if (hasPaid) {
                  return <p className={styles.dailyHint}>🎯 {credits} paid credit{credits !== 1 ? "s" : ""} remaining</p>;
                }

                // Free tier
                if (dailyLeft === 0) {
                  return (
                    <p className={`${styles.dailyHint} ${styles.dailyHintUrgent}`}
                       onClick={() => triggerUpgrade({ message: "You've used all free messages today" })}>
                      🔥 Free messages used up — <span className={styles.dailyHintLink}>upgrade to continue</span>
                    </p>
                  );
                }

                if (dailyLeft <= 2) {
                  return (
                    <p className={`${styles.dailyHint} ${styles.dailyHintWarn}`}
                       onClick={() => triggerUpgrade({ message: `Only ${dailyLeft} free message${dailyLeft !== 1 ? "s" : ""} left today` })}>
                      ⚡ {dailyLeft} free message{dailyLeft !== 1 ? "s" : ""} left today — <span className={styles.dailyHintLink}>most users upgrade here</span>
                    </p>
                  );
                }

                return <p className={styles.dailyHint}>{dailyLeft} of {dailyLimit} free AI messages left today</p>;
              })()}
            </div>
          </form>

        </div>
      </div>

      {/* ── RIGHT: OUTPUT PANEL (40%) ── */}
      <div className={styles.rightPanel}>
        <div className={styles.outputCard}>

          <div className={styles.outputHeaderRow}>
            <h3 className={styles.outputHeader}>🎯 Generated Message</h3>
            {isTemplate && message && (
              <span className={styles.templateBadge}>📄 Template</span>
            )}
          </div>

          {/* Response Score — compact, expandable */}
          {responseScore && message && (
            <div className={styles.scoreContainer}>
              <button
                type="button"
                className={styles.scoreHeader}
                onClick={() => setScoreExpanded((v) => !v)}
                aria-expanded={scoreExpanded}
              >
                <span className={styles.scoreLabel}>Response Probability</span>
                <span className={styles.scoreRight}>
                  <span
                    className={`${styles.scoreValue} ${
                      responseScore.score >= 80
                        ? styles.scoreHigh
                        : responseScore.score >= 50
                        ? styles.scoreMedium
                        : styles.scoreLow
                    }`}
                  >
                    {responseScore.score}%{" "}
                    {responseScore.score >= 80
                      ? "✅"
                      : responseScore.score >= 50
                      ? "🟡"
                      : "🔴"}
                  </span>
                  <span className={`${styles.scoreChevron} ${scoreExpanded ? styles.scoreChevronOpen : ""}`}>
                    ›
                  </span>
                </span>
              </button>
              <div className={styles.scoreBarTrack}>
                <div
                  className={`${styles.scoreBarFill} ${
                    responseScore.score >= 80
                      ? styles.scoreBarHigh
                      : responseScore.score >= 50
                      ? styles.scoreBarMedium
                      : styles.scoreBarLow
                  }`}
                  style={{ width: `${responseScore.score}%` }}
                />
              </div>
              {scoreExpanded && responseScore.breakdown && (
                <div className={styles.scoreBreakdown}>
                  {Object.values(responseScore.breakdown).map((item, i) => (
                    <span
                      key={i}
                      className={`${styles.scoreTag} ${
                        item.points > 0
                          ? styles.scoreTagPass
                          : styles.scoreTagFail
                      }`}
                    >
                      {item.points > 0 ? "✓" : "✗"} {item.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {!message ? (
            <div className={styles.emptyState}>
              <span className={styles.emptyStateIcon}>✉️</span>
              Fill in the form and hit <strong>Generate Message</strong> —<br />
              your personalised referral will appear here.
            </div>
          ) : (
            <div className={styles.outputContent}>
              <textarea
                id="output-message"
                className={styles.formTextarea}
                value={editedMessage}
                onChange={(e) => setEditedMessage(e.target.value)}
                aria-label="Generated referral message"
                spellCheck
              />

              {/* First-win tip — nudge to act fast */}
              {currentUser && !isTemplate && (
                <p className={styles.firstWinTip}>
                  📌 Tip: Send this within 24 hours for best response
                </p>
              )}

              {/* AI upsell strip — shown only for template results */}
              {isTemplate && (
                <div className={styles.upgradeStrip}>
                  <span className={styles.upgradeStripIcon}>✨</span>
                  <span className={styles.upgradeStripText}>
                    AI crafts messages that sound human, not templated
                  </span>
                  <div className={styles.upgradeStripActions}>
                    <Link to="/signup" className={styles.upgradeStripSignup}>
                      Sign Up Free
                    </Link>
                    <Link to="/login" className={styles.upgradeStripLogin}>
                      Log In
                    </Link>
                  </div>
                </div>
              )}

              <div className={styles.outputActions}>
                <button
                  id="btn-copy"
                  className={`${styles.copyButton} ${copied ? styles.copied : ""}`}
                  onClick={copy}
                  aria-live="polite"
                >
                  {copied ? "✓" : "📋"}<span className={styles.btnLabel}> {copied ? "Copied!" : "Copy"}</span>
                </button>
                <button
                  id="btn-followup"
                  className={styles.followUpButton}
                  onClick={handleFollowUp}
                  disabled={followUpLoading || loading}
                  title="Generate a follow-up message"
                >
                  {followUpLoading ? "⏳ Generating…" : "🔄 Follow Up"}
                </button>
                {editedMessage !== message && (
                  <button
                    className={styles.editButton}
                    onClick={() => setEditedMessage(message)}
                    title="Revert edits to original"
                  >
                    ↩<span className={styles.btnLabel}> Undo Edits</span>
                  </button>
                )}
                <button
                  id="btn-reset"
                  className={styles.editButton}
                  onClick={() => {
                    setFormData({
                      jobLink: "",
                      company: "",
                      role: "",
                      experience: "",
                      skills: "",
                      college: "",
                      targetPerson: "",
                    });
                    setMessage("");
                    setEditedMessage("");
                    setIsTemplate(false);
                    setPromo(null);
                    setPromoDismissed(false);
                    setResponseScore(null);
                    try { localStorage.removeItem(LAST_RECORD_KEY); } catch {}
                  }}
                  title="Clear all fields and output"
                >
                  ↻<span className={styles.btnLabel}> Reset</span>
                </button>

                {/* Remind-me — inline with action buttons */}
                {currentUser && message && (
                  <RemindMeButton
                    message={editedMessage}
                    company={formData.company}
                    role={formData.role}
                    targetPerson={formData.targetPerson}
                    onCreateReminder={createReminder}
                    reminders={reminders}
                    onReminderCreated={handleReminderCreated}
                  />
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── PROMO TOAST — slides up after a template response, non-blocking ── */}
      {promo && !promoDismissed && (
        <div className={styles.promoToast} role="complementary" aria-label="Upgrade prompt">
          <button
            className={styles.promoToastClose}
            onClick={() => setPromoDismissed(true)}
            aria-label="Dismiss"
          >
            ×
          </button>

          <p className={styles.promoToastHint}>{promo.hint}</p>
          <p className={styles.promoToastCta}>{promo.cta}</p>

          <div className={styles.promoToastActions}>
            <Link to="/signup" className={styles.promoToastSignup}>
              ✨ {promo.action}
            </Link>
            <Link to="/login" className={styles.promoToastLogin}>
              Already have an account?
            </Link>
          </div>
        </div>
      )}

    </div>
    </>
  );
};

export default ReferralForm;